import * as signalR from "@microsoft/signalr";
import Cookies from "js-cookie";
import socketService from '../Services/SocketService';

/**
 * Diagnostic utility for SignalR connection issues
 */
export class SignalRDiagnostics {
    constructor() {
        this.diagnostics = {
            timestamp: new Date().toISOString(),
            apiUrl: process.env.REACT_APP_API_URL,
            hasAccessToken: false,
            accessTokenValid: false,
            serverReachable: false,
            mainHubUrl: '',
            chatHubUrl: '',
            mainHubConnection: null,
            chatHubConnection: null,
            connectionAttempts: 0,
            errors: []
        };
    }

    /**
     * Initialize diagnostics with socket URLs
     */
    async initialize() {
        try {
            // Initialize socket service
            await socketService.getSocketBaseUrl();
            
            // Get hub URLs
            this.diagnostics.mainHubUrl = await socketService.getMainHubUrl();
            this.diagnostics.chatHubUrl = await socketService.getChatHubUrl();
            
        } catch (error) {
            this.diagnostics.errors.push(`Initialization error: ${error.message}`);
        }
    }

    /**
     * Run comprehensive diagnostics
     */
    async runDiagnostics() {
        await this.checkAccessToken();
        await this.checkServerReachability();
        await this.testMainHubConnection();
        await this.testChatHubConnection();

        this.printDiagnosticReport();
        return this.diagnostics;
    }

    /**
     * Check if access token exists and is valid
     */
    async checkAccessToken() {
        const token = Cookies.get("AccessToken");
        this.diagnostics.hasAccessToken = !!token;

        if (token) {
            // Basic token validation (check if it's a JWT)
            const tokenParts = token.split('.');
            this.diagnostics.accessTokenValid = tokenParts.length === 3;

            if (this.diagnostics.accessTokenValid) {
                try {
                    // Decode JWT payload (without verification)
                    const payload = JSON.parse(atob(tokenParts[1]));
                    const expiration = payload.exp * 1000; // Convert to milliseconds
                    const isExpired = Date.now() > expiration;

                    if (isExpired) {
                        this.diagnostics.errors.push('Access token has expired');
                    }
                } catch (error) {
                    this.diagnostics.errors.push('Could not decode token payload');
                }
            } else {
                this.diagnostics.errors.push('Token format appears invalid');
            }
        } else {
            this.diagnostics.errors.push('No access token found - user may not be logged in');
        }
    }

    /**
     * Check if the server is reachable
     */
    async checkServerReachability() {
        try {
            // Use SocketService to get proper URLs
            this.diagnostics.mainHubUrl = await socketService.getMainHubUrl();
            this.diagnostics.chatHubUrl = await socketService.getChatHubUrl();
            
            // Extract base URL for health check (remove hub path)
            const baseUrl = this.diagnostics.mainHubUrl.replace('/MainHub', '');

            try {
                const response = await fetch(`${baseUrl}/health`, {
                    method: 'GET',
                    mode: 'cors',
                    headers: {
                        'Authorization': `Bearer ${Cookies.get("AccessToken")}`,
                        'Content-Type': 'application/json'
                    }
                });

                this.diagnostics.serverReachable = response.ok;

                if (!response.ok) {
                    this.diagnostics.errors.push(`Server responded with status: ${response.status}`);
                }
            } catch (error) {
                this.diagnostics.errors.push(`Server not reachable: ${error.message}`);
            }
        } catch (error) {
            this.diagnostics.errors.push(`Error getting socket URLs: ${error.message}`);
        }
    }

    /**
     * Test MainHub connection (used by SmartMap)
     */
    async testMainHubConnection() {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(this.diagnostics.mainHubUrl, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
                accessTokenFactory: () => Cookies.get("AccessToken"),
            })
            .configureLogging(signalR.LogLevel.Debug)
            .build();

        try {
            this.diagnostics.connectionAttempts++;
            await connection.start();
            this.diagnostics.mainHubConnection = connection;

            // Test heartbeat method
            try {
                await connection.invoke("Heartbeat");
            } catch (heartbeatError) {
                // Heartbeat method not available
            }

            // Don't stop the connection - let it stay connected for SmartMap
            // await connection.stop();
        } catch (error) {
            this.diagnostics.errors.push(`MainHub connection failed: ${error.message}`);

            // Provide specific error guidance
            if (error.message.includes('CORS')) {
                this.diagnostics.errors.push('CORS error detected - check server CORS configuration');
            } else if (error.message.includes('401')) {
                this.diagnostics.errors.push('Authentication error - check access token');
            } else if (error.message.includes('404')) {
                this.diagnostics.errors.push('MainHub not found - check if MainHub endpoint exists');
            } else if (error.message.includes('WebSocket')) {
                this.diagnostics.errors.push('WebSocket error - check if WebSocket is enabled on server');
            }
        }
    }

    /**
     * Test ChatHub connection (used by ChatContext)
     */
    async testChatHubConnection() {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(this.diagnostics.chatHubUrl, {
                skipNegotiation: true,
                transport: signalR.HttpTransportType.WebSockets,
                accessTokenFactory: () => Cookies.get("AccessToken"),
            })
            .configureLogging(signalR.LogLevel.Debug)
            .build();

        try {
            this.diagnostics.connectionAttempts++;
            await connection.start();
            this.diagnostics.chatHubConnection = connection;

            // Test heartbeat method
            try {
                await connection.invoke("Heartbeat");
            } catch (heartbeatError) {
                // Heartbeat method not available
            }

            await connection.stop();
        } catch (error) {
            this.diagnostics.errors.push(`ChatHub connection failed: ${error.message}`);

            // Provide specific error guidance
            if (error.message.includes('CORS')) {
                this.diagnostics.errors.push('CORS error detected - check server CORS configuration');
            } else if (error.message.includes('401')) {
                this.diagnostics.errors.push('Authentication error - check access token');
            } else if (error.message.includes('404')) {
                this.diagnostics.errors.push('ChatHub not found - check if ChatHub endpoint exists');
            } else if (error.message.includes('WebSocket')) {
                this.diagnostics.errors.push('WebSocket error - check if WebSocket is enabled on server');
            }
        }
    }

    /**
     * Print diagnostic report
     */
    printDiagnosticReport() {
        // This method is kept for compatibility but no longer logs to console
        // The diagnostic data is still available through getResults()
    }

    /**
     * Get diagnostic results
     */
    getResults() {
        return this.diagnostics;
    }

    /**
     * Clean up connections
     */
    cleanup() {
        if (this.diagnostics.mainHubConnection) {
            this.diagnostics.mainHubConnection.stop();
        }
        if (this.diagnostics.chatHubConnection) {
            this.diagnostics.chatHubConnection.stop();
        }
    }
}

/**
 * Quick diagnostic function
 */
export const runQuickDiagnostics = async () => {
    const diagnostics = new SignalRDiagnostics();
    const results = await diagnostics.runDiagnostics();

    // Clean up after diagnostics
    setTimeout(() => {
        diagnostics.cleanup();
    }, 5000);

    return results;
};

/**
 * Check if user is logged in
 */
export const isUserLoggedIn = () => {
    const token = Cookies.get("AccessToken");
    if (!token) return false;

    try {
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) return false;

        const payload = JSON.parse(atob(tokenParts[1]));
        const expiration = payload.exp * 1000;
        return Date.now() < expiration;
    } catch {
        return false;
    }
};

/**
 * Get MainHub connection URL
 */
export const getMainHubUrl = async () => {
    try {
        return await socketService.getMainHubUrl();
    } catch (error) {
        // Fallback to default URL with proper concatenation
        const baseUrl = process.env.REACT_APP_API_URL;
        const apiUrl = baseUrl.includes("api/v1/")
            ? baseUrl.replace("api/v1/", "")
            : baseUrl;
        const normalizedBaseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
        return `${normalizedBaseUrl}MainHub`;
    }
};

/**
 * Get ChatHub connection URL
 */
export const getChatHubUrl = async () => {
    try {
        return await socketService.getChatHubUrl();
    } catch (error) {
        // Fallback to default URL with proper concatenation
        const baseUrl = process.env.REACT_APP_API_URL;
        const apiUrl = baseUrl.includes("api/v1/")
            ? baseUrl.replace("api/v1/", "")
            : baseUrl;
        const normalizedBaseUrl = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
        return `${normalizedBaseUrl}ChatHub`;
    }
};

/**
 * Get connection URL that would be used (for backward compatibility)
 */
export const getConnectionUrl = async () => {
    return await getChatHubUrl();
}; 